import React, { useState } from 'react';
import { useLayout } from '../../context/LayoutContext';
import { 
  Bell, 
  Settings2, 
  ChevronRight, 
  Filter, 
  CheckCircle2, 
  Circle,
  AlertTriangle,
  Info,
  Calendar,
  Zap
} from 'lucide-react';
import { TechnicalAlert, SIGNAL_TYPES, SIGNAL_EXPLANATIONS, SEVERITY_COLORS, SEVERITY_LABELS } from './TechnicalAlertsTypes';

interface MobileTechnicalAlertsProps {
  filteredAlerts: TechnicalAlert[];
  disabledSignals: Set<string>;
  filterType: string;
  setFilterType: (type: string) => void;
  filterSeverity: string;
  setFilterSeverity: (sev: string) => void;
  filterSymbol: string;
  setFilterSymbol: (sym: string) => void;
  filterDays: number;
  setFilterDays: (days: number) => void;
  stocks: { symbole: string; nom_societe?: string }[];
  currentPage: number;
  setCurrentPage: (page: number) => void;
  totalPages: number;
  totalAlerts: number;
  pageSize: number;
  setPageSize: (size: number) => void;
  loading: boolean;
  error: string;
  groupedByDate: Record<string, TechnicalAlert[]>;
  toggleSignal: (key: string) => void;
  enableAll: () => void;
  formatDate: (date: string) => string;
  getSignalInfo: (type: string) => any;
}

const MobileTechnicalAlerts: React.FC<MobileTechnicalAlertsProps> = ({
  filteredAlerts,
  disabledSignals,
  filterType,
  setFilterType,
  filterSeverity,
  setFilterSeverity,
  filterSymbol,
  setFilterSymbol,
  filterDays,
  setFilterDays,
  stocks,
  currentPage,
  setCurrentPage,
  totalPages,
  totalAlerts,
  pageSize,
  setPageSize,
  loading,
  error,
  groupedByDate,
  toggleSignal,
  enableAll,
  formatDate,
  getSignalInfo,
}) => {
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const { toggleMobileMenu } = useLayout();

  const toggleExplanation = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Group by category for settings
  const categories = ['Tendance', 'Oscillateur', 'Volume', 'Niveaux', 'Volatilité', 'Valorisation', 'Marché', 'Comportemental'];

  const stats = [
    { label: 'Signaux', value: filteredAlerts.length, color: 'var(--text-heading)' },
    { label: 'Priorité Haute', value: filteredAlerts.filter(a => a.severity === 'HIGH').length, color: '#ef4444' },
    { label: 'Filtres Actifs', value: SIGNAL_TYPES.length - disabledSignals.size, color: '#2563eb' },
    { label: 'Aujourd\'hui', value: Object.values(groupedByDate)[0]?.length || 0, color: '#16a34a' }
  ];

  return (
    <div className="mobile-alerts-container" style={{ padding: '0 12px 60px' }}>
      
      {/* Header avec burger + titre + bouton config */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        padding: '16px 4px',
        marginBottom: '4px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            type='button'
            onClick={toggleMobileMenu}
            style={{ background: 'none', border: 'none', padding: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
          >
            <i className='material-icons' style={{ fontSize: '24px', color: 'var(--text-heading)' }}>menu</i>
          </button>
          <h2 style={{ fontSize: '18px', fontWeight: 800, margin: 0, color: 'var(--text-heading)' }}>
            Derniers Signaux
          </h2>
        </div>
        <button 
          onClick={() => setShowSettingsModal(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 12px',
            borderRadius: '12px',
            border: '1px solid var(--border-color)',
            background: 'var(--card-bg)',
            color: 'var(--text-heading)',
            fontSize: '13px',
            fontWeight: 600,
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
          }}
        >
          <Settings2 size={16} />
          Configurer
        </button>
      </div>

      {/* KPI Grid 2x2 */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: '1fr 1fr', 
        gap: '10px', 
        marginBottom: '20px' 
      }}>
        {stats.map((stat, i) => (
          <div key={i} style={{
            background: 'var(--card-bg)',
            border: '1px solid var(--border-color)',
            borderRadius: '16px',
            padding: '12px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <span style={{ fontSize: '20px', fontWeight: 900, color: stat.color }}>{stat.value}</span>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.4px', marginTop: '2px' }}>
              {stat.label}
            </span>
          </div>
        ))}
      </div>

      {/* Filters Overlay Toggle/Visual */}
      <div style={{ 
        display: 'flex', 
        gap: '8px', 
        marginBottom: '20px',
        overflowX: 'auto',
        paddingBottom: '4px',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none'
      }}>
        {/* Stock Filter */}
        <select
          value={filterSymbol}
          onChange={e => setFilterSymbol(e.target.value)}
          style={{
            flexShrink: 0,
            padding: '8px 12px',
            borderRadius: '10px',
            fontSize: '12px',
            fontWeight: 600,
            border: '1px solid var(--border-color)',
            background: 'var(--card-bg)',
            color: 'var(--text-heading)',
            minWidth: '140px'
          }}
        >
          <option value="ALL">🏢 Toutes les sociétés ({stocks.length || 48})</option>
          {stocks.map(s => (
            <option key={s.symbole} value={s.symbole}>
              {s.symbole} {s.nom_societe ? `- ${s.nom_societe}` : ''}
            </option>
          ))}
        </select>

        {/* Type Filter */}
        <select
          value={filterType}
          onChange={e => setFilterType(e.target.value)}
          style={{
            flexShrink: 0,
            padding: '8px 12px',
            borderRadius: '10px',
            fontSize: '12px',
            border: '1px solid var(--border-color)',
            background: 'var(--card-bg)',
            color: 'var(--text-heading)',
            minWidth: '130px'
          }}
        >
          <option value="ALL">Tout Types</option>
          {SIGNAL_TYPES.filter(s => !disabledSignals.has(s.key)).map(s => (
            <option key={s.key} value={s.key}>{s.emoji} {s.label}</option>
          ))}
        </select>

        {/* Severity Filter */}
        <select
          value={filterSeverity}
          onChange={e => setFilterSeverity(e.target.value)}
          style={{
            flexShrink: 0,
            padding: '8px 12px',
            borderRadius: '10px',
            fontSize: '12px',
            border: '1px solid var(--border-color)',
            background: 'var(--card-bg)',
            color: 'var(--text-heading)',
            minWidth: '130px'
          }}
        >
          <option value="ALL">Toutes Priorités</option>
          <option value="HIGH">🔴 Haute</option>
          <option value="MEDIUM">🟡 Moyenne</option>
          <option value="LOW">⚪ Faible</option>
        </select>

        {/* History Horizon Filter */}
        <select
          value={filterDays}
          onChange={e => setFilterDays(Number(e.target.value))}
          style={{
            flexShrink: 0,
            padding: '8px 12px',
            borderRadius: '10px',
            fontSize: '12px',
            border: '1px solid var(--border-color)',
            background: 'var(--card-bg)',
            color: 'var(--text-heading)',
            minWidth: '140px'
          }}
        >
          <option value={7}>📅 7 jours</option>
          <option value={14}>📅 14 jours</option>
          <option value={30}>📅 1 mois (30j)</option>
          <option value={60}>📅 2 mois (60j)</option>
          <option value={90}>📅 3 mois (90j)</option>
        </select>
      </div>

      {/* Loading / Error */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-muted)' }}>
          <Zap size={32} className="animate-pulse" style={{ margin: '0 auto 12px', opacity: 0.5 }} />
          <p style={{ fontSize: '14px' }}>Chargement des opportunités...</p>
        </div>
      )}
      
      {error && (
        <div style={{ 
          background: 'rgba(239, 68, 68, 0.05)', 
          border: '1px solid rgba(239, 68, 68, 0.1)',
          borderRadius: '12px',
          padding: '20px',
          textAlign: 'center',
          color: '#ef4444'
        }}>
          <AlertTriangle size={32} style={{ margin: '0 auto 10px' }} />
          <p style={{ fontSize: '14px', margin: 0 }}>{error}</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && Object.keys(groupedByDate).length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-muted)' }}>
          <Bell size={48} style={{ margin: '0 auto 16px', opacity: 0.2 }} />
          <p style={{ fontWeight: 600, fontSize: '16px', marginBottom: '4px', color: 'var(--text-heading)' }}>Silence radio</p>
          <p style={{ fontSize: '13px' }}>Aucun signal n'a été détecté avec vos filtres actuels.</p>
        </div>
      )}

      {/* Grouped Alerts */}
      {!loading && !error && Object.entries(groupedByDate).map(([dateKey, dateAlerts]) => (
        <div key={dateKey} style={{ marginBottom: '24px' }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px', 
            marginBottom: '12px',
            padding: '0 4px'
          }}>
            <Calendar size={14} color="var(--text-muted)" />
            <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'capitalize' }}>
              {formatDate(dateKey)}
            </span>
            <div style={{ height: '1px', flex: 1, background: 'var(--border-color)', opacity: 0.5 }}></div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {dateAlerts.map(alert => {
              const info = getSignalInfo(alert.signal_type);
              const isExpanded = expandedIds.has(alert.id);
              const explanation = SIGNAL_EXPLANATIONS[alert.signal_type];
              return (
                <div
                  key={alert.id}
                  onClick={() => window.location.href = `/data?symbol=${alert.symbole}`}
                  style={{
                    background: 'var(--card-bg)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '16px',
                    padding: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
                  }}
                >
                  {/* Decorative severity indicator */}
                  <div style={{ 
                    position: 'absolute', 
                    top: 0, 
                    left: 0, 
                    bottom: 0, 
                    width: '4px',
                    background: SEVERITY_COLORS[alert.severity] || '#6b7280'
                  }}></div>

                  {/* Row 1: Icon + Symbol + Severity + Info button */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '20px' }}>{info.emoji}</span>
                      <span style={{ fontSize: '18px', fontWeight: 800, color: 'var(--text-heading)' }}>{alert.symbole}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {explanation && (
                        <button
                          onClick={(e) => toggleExplanation(alert.id, e)}
                          style={{
                            background: isExpanded ? 'rgba(37,99,235,0.1)' : 'none',
                            border: 'none', cursor: 'pointer',
                            color: isExpanded ? '#2563eb' : 'var(--text-muted)',
                            padding: '4px', borderRadius: '50%',
                            display: 'flex', alignItems: 'center',
                          }}
                        >
                          <Info size={16} />
                        </button>
                      )}
                      <div style={{ 
                        fontSize: '10px', 
                        fontWeight: 700, 
                        padding: '3px 8px', 
                        borderRadius: '6px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.4px',
                        background: `${SEVERITY_COLORS[alert.severity]}20`,
                        color: SEVERITY_COLORS[alert.severity]
                      }}>
                        {SEVERITY_LABELS[alert.severity]}
                      </div>
                    </div>
                  </div>

                  {/* Row 2: Signal Name */}
                  <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-heading)' }}>
                    {info.label}
                  </div>

                  {/* Row 3: Detail */}
                  <div style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                    {alert.signal_detail}
                  </div>

                  {/* Row 3b: Explanation (expandable) */}
                  {isExpanded && explanation && (
                    <div style={{
                      padding: '10px 12px',
                      background: 'var(--bg-secondary)',
                      borderRadius: '10px',
                      fontSize: '12px',
                      color: 'var(--text-secondary)',
                      lineHeight: 1.6,
                      borderLeft: '3px solid #2563eb',
                    }}>
                      {explanation}
                    </div>
                  )}

                  {/* Row 4: Bottom Stats + Action */}
                  <div style={{ 
                    marginTop: '4px',
                    paddingTop: '10px',
                    borderTop: '1px solid var(--border-color)',
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center' 
                  }}>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                      Cours : <strong style={{ color: 'var(--text-heading)' }}>{alert.price_at_signal?.toLocaleString('fr-FR')} FCFA</strong>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', color: '#2563eb', fontWeight: 600, fontSize: '13px', gap: '2px' }}>
                      Terminal <ChevronRight size={14} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Pagination Controls */}
      {!loading && !error && totalAlerts > 0 && (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px',
          padding: '16px 12px', background: 'var(--card-bg)', border: '1px solid var(--border-color)',
          borderRadius: '16px', marginTop: '16px', marginBottom: '24px'
        }}>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', textAlign: 'center' }}>
            Signaux <strong>{Math.min((currentPage - 1) * pageSize + 1, totalAlerts)}</strong> - <strong>{Math.min(currentPage * pageSize, totalAlerts)}</strong> sur <strong>{totalAlerts}</strong> ({filterDays}j)
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', justifyContent: 'center' }}>
            <button
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage(currentPage - 1)}
              style={{
                flex: 1, padding: '10px', borderRadius: '10px', fontSize: '13px', fontWeight: 600,
                border: '1px solid var(--border-color)', background: 'var(--bg-secondary)',
                color: currentPage <= 1 ? 'var(--text-muted)' : 'var(--text-heading)',
                cursor: currentPage <= 1 ? 'not-allowed' : 'pointer',
                opacity: currentPage <= 1 ? 0.5 : 1, textAlign: 'center'
              }}
            >
              ← Précédent
            </button>

            <span style={{ fontSize: '13px', fontWeight: 700, padding: '0 8px', color: 'var(--text-heading)', minWidth: '70px', textAlign: 'center' }}>
              {currentPage} / {totalPages}
            </span>

            <button
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage(currentPage + 1)}
              style={{
                flex: 1, padding: '10px', borderRadius: '10px', fontSize: '13px', fontWeight: 600,
                border: '1px solid var(--border-color)', background: 'var(--bg-secondary)',
                color: currentPage >= totalPages ? 'var(--text-muted)' : 'var(--text-heading)',
                cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer',
                opacity: currentPage >= totalPages ? 0.5 : 1, textAlign: 'center'
              }}
            >
              Suivant →
            </button>
          </div>
        </div>
      )}

      {/* Settings Modal (Overlay) */}
      {showSettingsModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1000,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'flex-end',
          backdropFilter: 'blur(4px)'
        }}>
          <div style={{
            width: '100%',
            maxHeight: '85vh',
            background: 'var(--card-bg)',
            borderTopLeftRadius: '24px',
            borderTopRightRadius: '24px',
            padding: '24px 20px 40px',
            overflowY: 'auto',
            animation: 'slideUp 0.3s ease-out'
          }}>
            <div style={{ 
              width: '40px', 
              height: '4px', 
              background: 'var(--border-color)', 
              borderRadius: '2px', 
              margin: '0 auto 20px' 
            }}></div>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: 'var(--text-heading)' }}>Configuration</h3>
              <button 
                onClick={enableAll}
                style={{ background: 'none', border: 'none', color: '#2563eb', fontSize: '13px', fontWeight: 700 }}
              >
                Tout Activer
              </button>
            </div>

            <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '24px' }}>
              Personnalisez votre flux : désactivez les signaux qui ne correspondent pas à votre stratégie.
            </p>

            {categories.map(cat => (
              <div key={cat} style={{ marginBottom: '24px' }}>
                <h4 style={{ 
                  fontSize: '11px', 
                  fontWeight: 800, 
                  color: 'var(--text-muted)', 
                  textTransform: 'uppercase', 
                  letterSpacing: '1px',
                  marginBottom: '12px'
                }}>
                  {cat}
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {SIGNAL_TYPES.filter(s => s.category === cat).map(sig => {
                    const isEnabled = !disabledSignals.has(sig.key);
                    return (
                      <div 
                        key={sig.key}
                        onClick={() => toggleSignal(sig.key)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '12px 16px',
                          borderRadius: '14px',
                          background: isEnabled ? 'rgba(37, 99, 235, 0.05)' : 'var(--bg-secondary)',
                          border: `1px solid ${isEnabled ? 'rgba(37, 99, 235, 0.1)' : 'var(--border-color)'}`,
                          transition: 'all 0.2s'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <span style={{ fontSize: '18px' }}>{sig.emoji}</span>
                          <span style={{ 
                            fontSize: '14px', 
                            fontWeight: 600, 
                            color: isEnabled ? 'var(--text-heading)' : 'var(--text-muted)' 
                          }}>
                            {sig.label}
                          </span>
                        </div>
                        {isEnabled ? (
                          <CheckCircle2 size={18} color="#2563eb" fill="rgba(37, 99, 235, 0.1)" />
                        ) : (
                          <Circle size={18} color="var(--text-muted)" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            <button
              onClick={() => setShowSettingsModal(false)}
              style={{
                width: '100%',
                padding: '16px',
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                color: '#fff',
                border: 'none',
                fontWeight: 700,
                fontSize: '15px',
                marginTop: '10px',
                boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)'
              }}
            >
              Terminer
            </button>
          </div>
        </div>
      )}

      {/* Global CSS for animations */}
      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: .5; }
        }
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        .mobile-alerts-container select {
          -webkit-appearance: none;
          background-image: url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23666666%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.4-12.8z%22%2F%3E%3C%2Fsvg%3E');
          background-repeat: no-repeat;
          background-position: right 10px top 50%;
          background-size: 8px auto;
        }
      `}</style>
    </div>
  );
};

export default MobileTechnicalAlerts;
